import { useState, useEffect, useCallback, useRef } from 'react';
import { parseCSV, buildDoaAssetRows, getSheetHeaders } from '../utils/csvParser';
import { SHEET_CONFIG, getSheetCsvUrl } from '../constants';

export const useDoaData = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      if (!silent) setRefreshing(true);

      setError('');

      const response = await fetch(
        getSheetCsvUrl(SHEET_CONFIG.DOA_SHEET, {
          bustCache: true,
        })
      );

      if (!response.ok) {
        throw new Error('Sheet fetch failed');
      }

      const rows = parseCSV(await response.text());

      if (!isMountedRef.current) return;

      if (!rows || rows.length === 0) {
        setHeaders([]);
        setData([]);
        return;
      }

      const hdrs = getSheetHeaders(rows);
      const dataRows = buildDoaAssetRows(rows, rows[0] || []);

      setHeaders(hdrs);
      setData(dataRows);
    } catch (err) {
      console.error('DOA data fetch error:', err);

      if (isMountedRef.current) {
        setError('Failed to load DOA data. Make sure the sheet is public.');
        setHeaders([]);
        setData([]);
      }
    } finally {
      isFetchingRef.current = false;

      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData({ silent: true });

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchData({ silent: true });
      }
    }, SHEET_CONFIG.REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  return {
    data,
    headers,
    loading,
    refreshing,
    error,
    refreshData: () => fetchData({ silent: false }),
  };
};